import { GVResult } from "./model";

/**
 *
 * @param {Date} date
 */
function toMinuteOfDay(date) {
  return date.getHours() * 60 + date.getMinutes();
}

/**
 * Tests whether a set is a superset of another set
 * @param {Set<any>} set
 * @param {Set<any>} subset
 * @returns {boolean}
 */
function isSuperset(set, subset) {
  var res = true;
  subset.forEach(function (elem, e2, s2) {
    if (!set.has(elem)) {
      res = false;
    }
  });
  return res;
}

/**
 * Computes the union of two sets
 * @param {Set<any>} setA
 * @param {Set<any>} setB
 * @returns {Set<any>}
 */
function union(setA, setB) {
  var union = new Set(setA);
  setB.forEach(function (elem, e2, s2) {
    union.add(elem);
  });
  return union;
}

/**
 * Computes the intersection of two sets
 * @param {Set<any>} setA
 * @param {Set<any>} setB
 * @returns {Set<any>}
 */
function intersection(setA, setB) {
  var intersection = new Set();
  setB.forEach(function (elem, e2, s2) {
    if (setA.has(elem)) {
      intersection.add(elem);
    }
  });
  return intersection;
}

/**
 * Computes the difference of two sets,
 * that is, which elements are in A but not in B
 * @param {Set<any>} setA
 * @param {Set<any>} setB
 * @returns {Set<any>}
 */
function difference(setA, setB) {
  var difference = new Set(setA);
  setB.forEach(function (elem, e2, s2) {
    difference.delete(elem);
  });
  return difference;
}

class GVFilterSettings {
  /**
   * Describes one of two things:
   *  * A set of filter settings as set by the user.
   *  or
   *  * The limits on the values the user can set, in which case the set fields are not EXCLUDED but rather
   *    summaries of what is available to choose from.
   */
  constructor(limit = false) {
    this.reset(limit);
  }

  /**
   * Resets the filter settings to the most general options
   */
  clear() {
    this.durationMax = this.durationLimit; //v
    this.departureMin = 0; //v
    this.departureMax = 86400; //v
    this.returnMin = 0; //v
    this.returnMax = 86400; //v
    this.excludedStops = new Set(); //v
    this.excludedOrigins = new Set(); //v
    this.excludedDestinations = new Set(); //v
    this.excludedAirlines = new Set(); //v
    this.priceMax = this.priceLimit; //v
  }

  /**
   * Prepares the filter settings for a new search
   */
  reset() {
    this.durationLimit = 1;
    this.durationMax = this.durationLimit; //v
    this.departureMin = 0; //v
    this.departureMax = 86400; //v
    this.returnMin = 0; //v
    this.returnMax = 86400; //v
    this.excludedStops = new Set(); //v
    this.excludedOrigins = new Set(); //v
    this.excludedDestinations = new Set(); //v
    this.excludedAirlines = new Set(); //v
    this.knownStops = new Set(); //v
    this.knownOrigins = new Set(); //v
    this.knownDestinations = new Set(); //v
    this.knownAirlines = new Set(); //v

    this.priceMax = this.priceLimit; //v
    this.priceLimit = 1;

    this.storedPriceMax = this.storedPriceLimit = this.priceLimit;
    this.storedDurationMax = this.storedDurationLimit = this.durationLimit;

    this.storedDestinations = new Set();
    this.storedOrigins = new Set();
    this.storedStops = new Set();
    this.storedAirlines = new Set();
  }

  /**
   * Tests whether a result matches the filter settings;
   * @param {GVResult} result
   */
  matches(result) {
    if (result.longestDuration > this.durationMax) return false;

    if (result.cheapestPrice > this.priceMax) return false;

    if (
      toMinuteOfDay(result.departureDate) < this.departureMin ||
      toMinuteOfDay(result.departureDate) > this.departureMax
    )
      return false;

    if (result.roundtrip) {
      if (
        toMinuteOfDay(result.returnDate) < this.returnMin ||
        toMinuteOfDay(result.returnDate) > this.returnMax
      )
        return false;
    }

    const stops = Math.min(result.mostStops, 2);

    return !(
      this.excludedStops.has(stops.toString()) ||
      this.excludedDestinations.has(result.destination) ||
      this.excludedOrigins.has(result.origin)
    );
  }

  /**
   * Extends the filter limit object to encompass the new result.
   * @param {GVResult} result
   */
  extendLimit(result) {
    /**
     * Elements which were disabled in the stored set S and which
     * are newly loaded ( part of E - O ), should also be disabled.
     *  This translates to D ∪ ( ( E - O ) ∩ S ), which is what is
     *  implemented here.
     * @param {Set<string>} e The current set of items
     * @param {Set<string>} o The old set of items
     * @param {Set<string>} s The set of stored  excluded items
     * @param {Set<string>} d The old excluded items
     * @returns {Set<string>} The new excluded items
     */
    function handleStoredSet(d, e, o, s) {
      return union(d, intersection(difference(e, o), s));
    }

    const oldDurationLimit = this.durationLimit;
    const oldPriceLimit = this.priceLimit;
    this.durationLimit = Math.max(this.durationLimit, result.longestDuration);
    this.priceLimit = Math.max(this.priceLimit, result.cheapestPrice);

    if (this.priceMax === oldPriceLimit) {
      if (this.storedPriceMax !== this.storedPriceLimit) {
        this.priceMax = Math.min(this.priceLimit, this.storedPriceMax);
      } else {
        this.priceMax = this.priceLimit;
      }
    }

    if (this.durationMax === oldDurationLimit) {
      if (this.storedDurationMax !== this.storedDurationLimit) {
        this.durationMax = Math.min(this.durationLimit, this.storedDurationMax);
      } else {
        this.durationMax = this.durationLimit;
      }
    }

    if (!this.knownDestinations.has(result.destination)) {
      this.knownDestinations.add(result.destination);
      if (this.storedDestinations.has(result.destination)) {
        this.excludedDestinations.add(result.destination);
      }
    }
    if (!this.knownOrigins.has(result.origin)) {
      this.knownOrigins.add(result.origin);
      if (this.storedOrigins.has(result.origin)) {
        this.excludedOrigins.add(result.origin);
      }
    }
    /*
        if ( this.knownAirlines, result.airlines ) ){
            this.knownAirlines.add( result.destination );
            if ( this.storedAirlines.has( result.destination ) ) {
                this.excludedAirlines.add( result.destination );
            }
        }*/
    if (!this.knownStops.has(result.mostStops)) {
      this.knownStops.add(result.mostStops);
      if (this.storedStops.has(result.mostStops)) {
        this.excludedStops.add(result.mostStops);
      }
    }
  }

  /**
   *
   * @param GVFilterSettings filter
   */
  loadStored(filter) {
    this.storedAirlines = new Set(filter.excludedAirlines);
    this.storedDestinations = new Set(filter.excludedDestinations);
    this.storedOrigins = new Set(filter.excludedOrigins);
    this.storedStops = new Set(filter.excludedStops);
    this.storedDurationLimit = filter.durationLimit;
    this.storedDurationMax = filter.durationMax;
    this.storedPriceLimit = filter.priceLimit;
    this.storedPriceMax = filter.priceMax;
    this.returnMin = filter.returnMin;
    this.returnMax = filter.returnMax;
    this.departureMax = filter.departureMax;
    this.departureMin = filter.departureMin;
  }

  toStoreable() {
    var result = {};
    result.returnMin = this.returnMin;
    result.returnMax = this.returnMax;
    result.departureMin = this.departureMin;
    result.departureMax = this.departureMax;
    result.priceMax = this.priceMax;
    result.priceLimit = this.priceLimit;
    result.durationMax = this.durationMax;
    result.durationLimit = this.durationLimit;
    result.excludedAirlines = Array.from(this.excludedAirlines);
    result.excludedDestinations = Array.from(this.excludedDestinations);
    result.excludedOrigins = Array.from(this.excludedOrigins);
    result.excludedStops = Array.from(this.excludedStops);
    return result;
  }

  clone() {
    var b = new GVFilterSettings();
    b.durationLimit = this.durationLimit;
    b.durationMax = this.departureMax;
    b.priceLimit = this.priceLimit;
    b.priceMax = this.priceMax;
    b.departureMin = this.departureMin;
    b.departureMax = this.departureMax;
    b.returnMin = this.returnMin;
    b.returnMax = this.returnMax;
    b.knownDestinations = new Set(this.knownDestinations);
    b.knownOrigins = new Set(this.knownOrigins);
    b.knownAirlines = new Set(this.knownAirlines);
    b.knownStops = new Set(this.knownStops);
    b.excludedDestinations = new Set(this.excludedDestinations);
    b.excludedOrigins = new Set(this.excludedOrigins);
    b.excludedAirlines = new Set(this.excludedAirlines);
    b.excludedStops = new Set(this.excludedStops);
    return b;
  }
}

export class GVFilterManager {
  constructor() {
    this._currentFilter = new GVFilterSettings();
    this.storedFilter = new GVFilterSettings();
    this.onFiltersChanged = () => {
      return 0;
    };
  }

  initialize(destination, roundtrip) {
    this.destination = destination;
    this.roundtrip = roundtrip;
    this.load();
  }

  applyFilters(results) {
    return results.filter((r) => this._currentFilter.matches(r));
  }

  updateFilters() {
    this.store();
    this.onFiltersChanged();
  }

  /**
   *
   * @param {GVResult[]} results
   */
  updateLimits(results) {
    for (let a of results) {
      this._currentFilter.extendLimit(a);
    }
  }

  get currentFilter() {
    return this._currentFilter;
  }

  get currentFilterMutable() {
    return this._currentFilter;
  }

  store() {
    localStorage.filter = JSON.stringify(this.currentFilter.toStoreable());
    localStorage.filterRoundtrip = this.roundtrip;
    localStorage.filterTimestamp = new Date();
    localStorage.filterDestination = this.destination;
  }

  load() {
    if (localStorage.filterDestination !== this.destination) {
      console.log(
        "not loading filters (dest): ",
        localStorage.filterDestination,
        this.destination
      );
      return;
    } else if (
      new Date().getTime() - new Date(localStorage.filterTimestamp).getTime() >
      3600 * 1000
    ) {
      console.log(
        "not loading filters (time): ",
        new Date().getTime(),
        new Date(localStorage.filterTimestamp).getTime()
      );
      return;
    }
    try {
      this.storedFilter = JSON.parse(localStorage.filter);
      this._currentFilter.loadStored(this.storedFilter);
    } catch (ex) {}

    console.log("loaded filters: ", this.storedFilter);
  }
}
